import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { scoreboardWeek, type MoneyBoard } from '@ff/core';
import type { SeasonContext } from '../lib/seasonContext.ts';
import {
  createShareLink,
  fetchOverrides,
  saveOverrides,
  syncSeasonNow,
  type Overrides,
} from '../lib/api.ts';
import { money as fmtMoney, relativeTime } from '../lib/format.ts';
import { useCurrentSeason } from '../lib/currentSeason.tsx';
import { AwardEligibility } from '../components/AwardEligibility.tsx';

/**
 * Commissioner-only controls: mint a public share link for a week, and record
 * the things ESPN can't tell us (final placements, who got Busted). Only
 * reachable when the signed-in member is a commissioner.
 */
export function Commissioner() {
  const { stored, nameOf } = useOutletContext<SeasonContext>();
  const season = stored.bundle.data.season;
  const { data, money } = stored.bundle;

  const owners = useMemo(
    () => [...data.owners].sort((a, b) => a.name.localeCompare(b.name)),
    [data],
  );
  const completed = useMemo(
    () => [...data.completedWeeks].sort((a, b) => b - a),
    [data],
  );

  // Rosters to check for movement, newest first. currentWeek is the upcoming
  // one between games — where a Tuesday trade shows up — and the scoreboard's
  // week is the fallback when that week has no stored lineups yet.
  const rosterWeeks = useMemo(() => {
    const board = scoreboardWeek(data);
    return [...new Set([data.currentWeek, board].filter((w): w is number => !!w))].sort(
      (a, b) => b - a,
    );
  }, [data]);

  // The manual state isn't part of the season bundle — it's what the
  // commissioner has recorded on top of it.
  //
  // A failed read must NOT become `{}`. Every save here replaces a whole field,
  // so a form built on an empty baseline would delete exclusions the league had
  // already recorded — and change who owes $25 — the moment anyone touched a
  // checkbox. Better to say the read failed and offer to try again.
  const [overrides, setOverrides] = useState<Overrides | null>(null);
  const [overridesError, setOverridesError] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let mounted = true;
    setOverridesError(false);
    fetchOverrides(season)
      .then((o) => mounted && setOverrides(o))
      .catch(() => mounted && setOverridesError(true));
    return () => {
      mounted = false;
    };
  }, [season, reload]);

  return (
    <div className="space-y-10">
      <div>
        <p className="eyebrow">{season} · commissioner</p>
        <h1 className="mt-1 font-display text-3xl font-black uppercase tracking-tight">
          The Back Office
        </h1>
      </div>

      <SyncSection season={season} syncedAt={stored.syncedAt} />
      <BuyInSection season={season} owners={owners} money={money} />
      <ShareSection season={season} weeks={completed} />
      {overridesError && (
        <section className="space-y-2">
          <h2 className="eyebrow">Clown eligibility</h2>
          <div className="card flex flex-wrap items-center justify-between gap-3 p-4">
            <p className="text-sm text-chalk-dim">
              Couldn't load what's been recorded, so this is hidden — editing it
              blind would wipe it.
            </p>
            <button
              onClick={() => setReload((n) => n + 1)}
              className="rounded-md border border-felt-line px-3 py-1.5 font-mono text-xs uppercase tracking-wider text-chalk hover:border-brass"
            >
              Try again
            </button>
          </div>
        </section>
      )}
      {overrides !== null &&
        stored.bundle.firstRound.length > 0 &&
        // One panel per draft award the league lets a commissioner rule on.
        stored.bundle.money.config.awards
          .filter(
            (a) => a.compute?.kind === 'draftPickExtreme' && a.compute.eligibility === 'manual',
          )
          .map((a) => (
            <AwardEligibility
              key={a.id}
              season={season}
              awardId={a.id}
              label={a.label}
              amount={a.amount}
              pick={a.compute.kind === 'draftPickExtreme' ? a.compute.pick : 'low'}
              weeks={rosterWeeks}
              firstRound={stored.bundle.firstRound}
              rulings={overrides.ineligible ?? {}}
              nameOf={nameOf}
            />
          ))}
      <OverridesSection
        season={season}
        owners={owners}
        money={money}
        nameOf={nameOf}
      />
    </div>
  );
}

/**
 * Pull the season from ESPN by hand.
 *
 * The server syncs itself every few minutes during NFL game windows — Thursday
 * night, Sunday, Monday night — and does nothing outside them. Draft night is a
 * Wednesday, so the picks sit on ESPN until something asks for them. This is the
 * something.
 */
function SyncSection({ season, syncedAt }: { season: number; syncedAt: string }) {
  const { refresh } = useCurrentSeason();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const { syncedAt: at } = await syncSeasonNow(season);
      // Pull the fresh bundle straight into the app so the boards update behind
      // this page instead of asking for a reload.
      await refresh();
      setDone(at);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Sync failed');
    } finally {
      setBusy(false);
    }
  }

  const lastSynced = relativeTime(done ?? syncedAt);

  return (
    <section className="space-y-3">
      <h2 className="eyebrow">ESPN data</h2>
      <p className="text-sm text-chalk-dim">
        The book refreshes itself during games. Everything else — draft picks the
        night of the draft, a stat correction on a Tuesday — waits for this.
      </p>
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={run}
          disabled={busy}
          className="rounded-md bg-brass px-3 py-1.5 font-display text-sm font-bold text-chalk hover:opacity-90 disabled:opacity-50"
        >
          {busy ? 'Pulling from ESPN…' : 'Sync now'}
        </button>
        {/* relativeTime returns '' for a missing or unparseable date, which would
            otherwise render as a dangling "last synced". */}
        <span className="font-mono text-xs text-chalk-dim">
          {lastSynced ? `last synced ${lastSynced}` : 'never synced'}
        </span>
      </div>
      {done && <p className="font-mono text-xs text-cash">synced. the boards are current.</p>}
      {error && <p className="font-mono text-xs text-blood">{error}</p>}
    </section>
  );
}

function BuyInSection({
  season,
  owners,
  money,
}: {
  season: number;
  owners: { id: string; name: string }[];
  money: MoneyBoard;
}) {
  const [boughtIn, setBoughtIn] = useState<Record<string, boolean>>(() =>
    // Coerce to a real boolean — a season doc cached before buy-in tracking has
    // no `boughtIn` flag on its owners, which would otherwise seed `undefined`.
    Object.fromEntries(money.owners.map((o) => [o.ownerId, o.boughtIn === true])),
  );
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const inCount = Object.values(boughtIn).filter(Boolean).length;
  const collected = inCount * money.config.buyIn;

  async function save() {
    setStatus('saving');
    try {
      await saveOverrides(season, { boughtIn });
      setStatus('saved');
      setTimeout(() => setStatus('idle'), 2000);
    } catch {
      setStatus('error');
    }
  }

  return (
    <section className="space-y-3">
      <h2 className="eyebrow">Buy-ins</h2>
      <p className="text-sm text-chalk-dim">
        Check off everyone who's paid their {fmtMoney(money.config.buyIn)} into the
        pot. The pot on the board stays at the full {fmtMoney(money.pot)} — this just
        tracks who's actually settled up.
      </p>
      <div className="card divide-y divide-felt-line p-1">
        {owners.map((o) => {
          const paid = boughtIn[o.id] === true;
          return (
            <button
              key={o.id}
              onClick={() => setBoughtIn((prev) => ({ ...prev, [o.id]: !paid }))}
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-felt"
            >
              <span className="font-display font-bold">{o.name}</span>
              <span
                className={`flex items-center gap-2 font-mono text-xs ${
                  paid ? 'text-cash' : 'text-chalk-dim'
                }`}
              >
                {paid ? 'paid in' : 'owes'}
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded border text-xs ${
                    paid ? 'border-cash bg-cash/20 text-cash' : 'border-felt-line text-transparent'
                  }`}
                >
                  ✓
                </span>
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={save}
          disabled={status === 'saving'}
          className="rounded-md bg-brass px-3 py-1.5 font-display text-sm font-bold text-chalk hover:opacity-90 disabled:opacity-50"
        >
          {status === 'saving' ? 'Saving…' : 'Save buy-ins'}
        </button>
        <span className="font-mono text-xs text-chalk-dim tabular">
          {fmtMoney(collected)} in · {inCount} of {owners.length} paid
        </span>
        {status === 'saved' && (
          <span className="font-mono text-xs text-cash">saved. refresh to see it.</span>
        )}
        {status === 'error' && <span className="font-mono text-xs text-blood">save failed</span>}
      </div>
    </section>
  );
}

function ShareSection({ season, weeks }: { season: number; weeks: number[] }) {
  const [week, setWeek] = useState<number | null>(weeks[0] ?? null);
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  async function mint() {
    if (week == null) return;
    setBusy(true);
    setUrl(null);
    try {
      setUrl((await createShareLink(season, week)).url);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="space-y-3">
      <h2 className="eyebrow">Share link</h2>
      <p className="text-sm text-chalk-dim">
        Mints a public link for one week. Paste it in the group text and it
        unfurls into the graphic. Anyone with the link can see that week; the
        rest of the book stays private.
      </p>
      {weeks.length === 0 ? (
        <p className="text-sm text-chalk-dim">No completed weeks to share yet.</p>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={week ?? ''}
            onChange={(e) => setWeek(Number(e.target.value))}
            className="rounded-md border border-felt-line bg-felt-raised px-2 py-1 font-mono text-sm text-chalk"
          >
            {weeks.map((w) => (
              <option key={w} value={w}>
                Week {w}
              </option>
            ))}
          </select>
          <button
            onClick={mint}
            disabled={busy}
            className="rounded-md bg-brass px-3 py-1.5 font-display text-sm font-bold text-chalk hover:opacity-90 disabled:opacity-50"
          >
            {busy ? 'Minting…' : 'Create link'}
          </button>
        </div>
      )}
      {url && (
        <div className="card flex items-center justify-between gap-3 p-3">
          <code className="truncate font-mono text-xs text-chalk">{url}</code>
          <button
            onClick={() => {
              navigator.clipboard.writeText(url);
              setCopied(true);
              setTimeout(() => setCopied(false), 1500);
            }}
            className="shrink-0 font-mono text-xs text-brass hover:opacity-80"
          >
            {copied ? 'copied' : 'copy'}
          </button>
        </div>
      )}
    </section>
  );
}

/** Just the parts of the money board this form reads and writes back. */
type MoneyLike = Pick<MoneyBoard, 'placements' | 'awards' | 'config'>;

function OverridesSection({
  season,
  owners,
  money,
  nameOf,
}: {
  season: number;
  owners: { id: string; name: string }[];
  money: MoneyLike;
  nameOf: (id: string) => string;
}) {
  // Every award nobody can compute — a human records the winner. Today that is
  // only Busted, but a league's own config decides.
  const manualRules = money.config.awards.filter((a) => a.compute?.kind === 'manual');
  const [placements, setPlacements] = useState<Record<number, string>>(() =>
    Object.fromEntries(
      money.placements.map((p) => [p.place, p.ownerId ?? '']),
    ),
  );
  const [manual, setManual] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      manualRules.map((r) => [
        r.id,
        money.awards.find((a) => a.id === r.id)?.winnerIds[0] ?? '',
      ]),
    ),
  );
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  async function save() {
    setStatus('saving');
    try {
      const placementEntries = Object.entries(placements).filter(([, v]) => v);
      await saveOverrides(season, {
        placements: Object.fromEntries(placementEntries.map(([k, v]) => [Number(k), v])),
        // The whole map, every time: the stored field is replaced wholesale,
        // so posting one award's winner would erase the others'.
        manualWinners: Object.fromEntries(
          Object.entries(manual).filter(([, ownerId]) => ownerId),
        ),
      });
      setStatus('saved');
      setTimeout(() => setStatus('idle'), 2000);
    } catch {
      setStatus('error');
    }
  }

  const OwnerSelect = ({
    value,
    onChange,
  }: {
    value: string;
    onChange: (id: string) => void;
  }) => (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="rounded-md border border-felt-line bg-felt-raised px-2 py-1 font-mono text-sm text-chalk"
    >
      <option value="">(none)</option>
      {owners.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  );

  return (
    <section className="space-y-3">
      <h2 className="eyebrow">Record the results</h2>
      <p className="text-sm text-chalk-dim">
        The things ESPN can't tell us: the final placements, and the awards
        only a human can call. Saving updates the money board for everyone.
      </p>
      <div className="card space-y-3 p-4">
        {money.placements
          .slice()
          .sort((a, b) => a.place - b.place)
          .map((p) => (
            <div key={p.place} className="flex items-center justify-between gap-3">
              <span className="font-display font-bold">{p.label}</span>
              <OwnerSelect
                value={placements[p.place] ?? ''}
                onChange={(id) => setPlacements((prev) => ({ ...prev, [p.place]: id }))}
              />
            </div>
          ))}
        {manualRules.map((r) => (
          <div
            key={r.id}
            className="flex items-center justify-between gap-3 border-t border-felt-line pt-3"
          >
            <span className="font-display font-bold">{r.label}</span>
            <OwnerSelect
              value={manual[r.id] ?? ''}
              onChange={(id) => setManual((prev) => ({ ...prev, [r.id]: id }))}
            />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={status === 'saving'}
          className="rounded-md bg-brass px-3 py-1.5 font-display text-sm font-bold text-chalk hover:opacity-90 disabled:opacity-50"
        >
          {status === 'saving' ? 'Saving…' : 'Save results'}
        </button>
        {status === 'saved' && (
          <span className="font-mono text-xs text-cash">saved. refresh to see it.</span>
        )}
        {status === 'error' && <span className="font-mono text-xs text-blood">save failed</span>}
        {manualRules
          .filter((r) => manual[r.id])
          .map((r) => (
            <span key={r.id} className="font-mono text-xs text-chalk-dim">
              {r.label}: {nameOf(manual[r.id]!)}
            </span>
          ))}
      </div>
    </section>
  );
}
